import { useState, memo } from "react";
import { Icon } from "@/components/icons/Icon";
import { literals } from "@/constants/literals";
import { BASE_URL } from "@/constants/constants";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import Modal from "@/components/modal/Modal";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/styles.utils";
import IconButton from "@/components/buttons/IconButton";
import ExplandableHeaderDetail from "./ExplandableHeaderDetail";
import { formatOptionalText } from "@/lib/texts.utils";
import { useDevice } from "@/store/useDevice";

export interface ExpandableOrderHeaderProps {
  stand: "reception" | "exits";
  basicInformation?: [
    { label: string; value: string },
    { label: string; value: string },
  ];
  expandedInformation?: { label: string; value: string | undefined }[];
  newOrderText?: boolean;
  inReceptionResumePage?: boolean;
  thereAreMoreGroups: boolean;
}

const ExpandableOrderHeader = ({
  stand,
  newOrderText,
  basicInformation,
  expandedInformation,
  inReceptionResumePage,
  thereAreMoreGroups,
}: ExpandableOrderHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const [leaveModal, setLeaveModal] = useState<boolean>(false);
  const [showEntireHeader, setShowEntireHeader] = useState<boolean>(false);

  const backToStandSelector = () => navigate(`${BASE_URL}/capture`);
  const switchLeaveModal = () =>
    setLeaveModal((previousState) => !previousState);

  const mustShowButton = () =>
    thereAreMoreGroups &&
    !(
      location.pathname.endsWith("/resume") ||
      location.pathname.endsWith("/resume/")
    );

  const goToOrderResume = () => navigate(`${BASE_URL}/capture/${stand}/resume`);

  const goToPackaging = () => {
    if (stand === "reception") {
      navigate(`${BASE_URL}/capture/${stand}/packaging`);
    }
  };

  const topInformation = () => {
    if (!basicInformation) return [];
    if (!isMobile && expandedInformation)
      return [...basicInformation, ...expandedInformation.slice(0, 2)];
    return basicInformation;
  };

  const bottomInformation = () => {
    if (!expandedInformation) return [];
    if (isMobile) return expandedInformation;
    return expandedInformation.slice(2);
  };

  return (
    <>
      <div
        className={cn(
          "flex flex-col w-full py-4 bg-ib-pt-color-neutral-100 ",
          newOrderText ? "h-[94px]" : "h-[138px]",
          !showEntireHeader ? "border-b border-b-ib-pt-color-gray-100" : "",
        )}
      >
        <div className="h-8 mb-1 flex flex-row justify-between px-4 md:px-14">
          <button
            type="button"
            onClick={switchLeaveModal}
            className="h-full text-ib-pt-color-red-500 flex flex-row items-center text-ib-pt-main"
          >
            <Icon name="backArrow" className="size-4 mr-4" />
            <span>{literals.leaveOrder}</span>
          </button>
          {mustShowButton() ? (
            <div>
              <SecondaryButton
                colour="red"
                size="small"
                icon="searchLens"
                label={literals.seeOrder}
                onClick={goToOrderResume}
              />
            </div>
          ) : null}
          {inReceptionResumePage ? (
            <div>
              <SecondaryButton
                colour="red"
                size="small"
                icon="pencil"
                label={literals.editPackaging}
                onClick={goToPackaging}
              />
            </div>
          ) : null}
        </div>
        {newOrderText ? (
          <h1 className="h-8 text-[24px] leading-[26px] font-ib-bold px-4 md:px-14">
            {literals.newOrder}
          </h1>
        ) : (
          <>
            <div className="flex-1 text-[24px] leading-[26px] px-4 md:px-14 font-ib-bold w-full flex flex-row justify-between items-center gap-x-2">
              <div className="w-screen">
                <div className="w-calc(100%-32px) flex flex-row justify-between items-center bg-ib-pt-color-neutral-100 gap-x-2">
                  {topInformation().map((info) => (
                    <ExplandableHeaderDetail
                      key={info.label}
                      label={info.label}
                      value={info.value || "-"}
                    />
                  ))}
                </div>
                {showEntireHeader && (
                  <div className="w-screen pl-4 md:pl-14 pr-12 md:pr-[88px] z-50 absolute left-0 translate-y-[16px] bg-ib-pt-color-neutral-100 shadow-[0px_6px_6px_0px_#0000001F]">
                    <div className="w-full flex flex-row justify-between md:justify-start items-center pb-4 flex-wrap gap-y-4 md:gap-x-2">
                      {bottomInformation().map((detail) => (
                        <ExplandableHeaderDetail
                          label={detail.label}
                          value={formatOptionalText(detail.value)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <IconButton
                  className="text-ib-pt-color-red-500 p-0 bg-transparent"
                  iconClassName="size-6"
                  icon={showEntireHeader ? "chevronUp" : "chevronDown"}
                  onClick={() => setShowEntireHeader(!showEntireHeader)}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <Modal
        title={literals.leaveOrder}
        opened={leaveModal}
        onClose={switchLeaveModal}
      >
        <p className="mb-8 text-ib-pt-main text-ib-pt-color-gray-1000">
          {literals.leaveOrderQuestion}
        </p>
        <div className="mb-4">
          <PrimaryButton
            colour="red"
            label={literals.leave}
            onClick={backToStandSelector}
            size="medium"
            icon="exit"
          />
        </div>
        <div>
          <SecondaryButton
            colour="red"
            label={literals.cancel}
            onClick={switchLeaveModal}
            size="medium"
          />
        </div>
      </Modal>
    </>
  );
};
export default memo(ExpandableOrderHeader);
